import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import DialogActions from "@material-ui/core/DialogActions";
import MuiDialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import Button from "@material-ui/core/Button";
import { AddNewSalePlatformIntegrationButton } from "src/components/AddNewSalePlatformIntegrationDialog";

import { getPoshmarkUser } from "src/utils/extension/poshmark";
import type { Cookie } from "src/utils/extension";
import {
  getPoshmarkIntegration,
  poshmarkIntegrationCreate,
} from "src/apiService/modules/poshmark";

import { StatefulLoadableButton } from "src/components/LoadableButton";
import DialogTitle from "./DialogTitle";
import type Step from "./Step";
import {
  completeUserSetupQuestionCardPlatform,
  doesUserHaveCardPlatform,
} from "src/apiService/modules/users";
import { useDispatch } from "react-redux";
import { poshmarkSyncSales } from "src/store/integrationSync/actions/poshmark";
import MonthYear from "src/interfaces/monthYear.interface";
import { useRecordEvent } from "src/hooks/useRecordEvent";

interface PoshmarkConfirmProps {
  onClose: () => void;
  onStepChange: (step: Step) => void;
  showConnectSalePlatform: boolean;
}

function PoshmarkConfirm({
  onClose,
  onStepChange,
  showConnectSalePlatform = true,
}: PoshmarkConfirmProps) {
  const dispatch = useDispatch();
  const recordEvent = useRecordEvent();
  const [poshmarkUserData, setPoshmarkUserData] = useState<
    | {
        username: string;
        jwt: string;
        cookies: Cookie[];
      }
    | { error: string }
    | { loading: true }
  >({ loading: true });

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const data = await getPoshmarkUser();
        if (cancel) return;
        setPoshmarkUserData(data);
      } catch (e) {
        if (cancel) return;
        setPoshmarkUserData({ error: (e as Error).message });
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  const onConnect = async (bankAccountId?: string) => {
    if ("jwt" in poshmarkUserData && poshmarkUserData.jwt) {
      const existingPoshmarkIntegration = await getPoshmarkIntegration();
      if (existingPoshmarkIntegration?.id) {
        if (await doesUserHaveCardPlatform("Poshmark")) {
          await completeUserSetupQuestionCardPlatform("Poshmark");
          const currentDate = new Date();
          dispatch(
            poshmarkSyncSales(existingPoshmarkIntegration.id, {
              start: {
                month: 1,
                year: currentDate.getFullYear() - 1,
              },
              end: {
                month: currentDate.getMonth() + 1,
                year: currentDate.getFullYear(),
              } as MonthYear,
            })
          );
        }
        toast.info("That Poshmark account is already connected.");
        onStepChange("poshmark:import");
        onClose();
        return;
      }

      await poshmarkIntegrationCreate({
        cookies: poshmarkUserData.cookies,
        username: poshmarkUserData?.username,
        lastSync: new Date().toISOString(),
        bankAccountId,
      });
      recordEvent({
        type: "feature-adoption",
        feature: "integration-added",
        element: "sale-platform-poshmark",
      });
      onStepChange("poshmark:import");
      if (await doesUserHaveCardPlatform("Poshmark")) {
        const poshmarkIntegration = await getPoshmarkIntegration();
        await completeUserSetupQuestionCardPlatform("Poshmark");
        if (poshmarkIntegration?.id) {
          const currentDate = new Date();
          dispatch(
            poshmarkSyncSales(poshmarkIntegration.id, {
              start: {
                month: 1,
                year: currentDate.getFullYear() - 1,
              },
              end: {
                month: currentDate.getMonth() + 1,
                year: currentDate.getFullYear(),
              } as MonthYear,
            })
          );
        }
      }
      onClose();
    }
  };

  return (
    <>
      <DialogTitle onClose={onClose}>Add Poshmark integration</DialogTitle>
      <MuiDialogContent>
        <DialogContentText>
          {"username" in poshmarkUserData
            ? `The Poshmark account "${poshmarkUserData.username}" will be added.`
            : "error" in poshmarkUserData
            ? `ERROR: ${poshmarkUserData.error}`
            : "Loading..."}
        </DialogContentText>
      </MuiDialogContent>
      <DialogActions>
        <Button
          onClick={() => onStepChange("poshmark:do-you-sell-on-poshmark")}
        >
          Cancel
        </Button>
        {showConnectSalePlatform ? (
          <AddNewSalePlatformIntegrationButton
            variant="contained"
            disabled={
              !!(poshmarkUserData as any).error ||
              (poshmarkUserData as any).loading
            }
            onConnect={onConnect}
            color="primary"
            autoFocus
          >
            Add
          </AddNewSalePlatformIntegrationButton>
        ) : (
          <StatefulLoadableButton
            color="primary"
            variant="contained"
            disabled={
              !!(poshmarkUserData as any).error ||
              (poshmarkUserData as any).loading
            }
            onClick={async () => await onConnect()}
          >
            Connect
          </StatefulLoadableButton>
        )}
      </DialogActions>
    </>
  );
}

export default PoshmarkConfirm;
